/**
 * First-touch campaign attribution for the tab session.
 *
 * Stored in sessionStorage, not a cookie: it lasts for this tab, including the
 * Stripe redirect back, and dies when the tab closes. It is written on landing
 * whether or not analytics cookies were accepted, because the checkout request
 * is first-party and the webhook is the attribution path when PostHog itself
 * must stay memory-only. Nothing here is sent to PostHog until capture is
 * already permitted.
 *
 * First touch wins. In-app navigation drops the query string; reading the URL
 * again at form submit would record an empty campaign.
 */

const STORAGE_KEY = "otalytics_first_touch";
const LEAD_KEY = "otalytics_lead_ids";

export interface FirstTouchAttribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  referrerHost?: string;
}

function hasWindow(): boolean {
  return typeof window !== "undefined";
}

function readQuery(): FirstTouchAttribution {
  const params = new URLSearchParams(window.location.search);
  const attribution: FirstTouchAttribution = {};
  const source = params.get("utm_source");
  const medium = params.get("utm_medium");
  const campaign = params.get("utm_campaign");
  const content = params.get("utm_content");
  const term = params.get("utm_term");
  if (source) attribution.utmSource = source.slice(0, 64);
  if (medium) attribution.utmMedium = medium.slice(0, 64);
  if (campaign) attribution.utmCampaign = campaign.slice(0, 128);
  if (content) attribution.utmContent = content.slice(0, 128);
  if (term) attribution.utmTerm = term.slice(0, 128);

  if (document.referrer) {
    try {
      const { hostname } = new URL(document.referrer);
      if (hostname && hostname !== window.location.hostname) {
        attribution.referrerHost = hostname.slice(0, 253);
      }
    } catch {
      // A malformed referrer is not worth failing a landing over.
    }
  }
  return attribution;
}

function hasSignal(value: FirstTouchAttribution): boolean {
  return Boolean(
    value.utmSource ||
      value.utmMedium ||
      value.utmCampaign ||
      value.utmContent ||
      value.utmTerm ||
      value.referrerHost
  );
}

function readStored(): FirstTouchAttribution | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    return parsed as FirstTouchAttribution;
  } catch {
    return null;
  }
}

/** Call once per page load. Does not overwrite a touch already stored. */
export function captureFirstTouch(): void {
  if (!hasWindow()) {
    return;
  }
  const existing = readStored();
  if (existing && hasSignal(existing)) {
    return;
  }
  const current = readQuery();
  if (!hasSignal(current)) {
    return;
  }
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    // Private mode can throw. The current URL is still readable at submit.
  }
}

/** Stored first touch, or the current URL when this tab has none yet. */
export function readFirstTouch(): FirstTouchAttribution {
  if (!hasWindow()) {
    return {};
  }
  const stored = readStored();
  if (stored && hasSignal(stored)) {
    return stored;
  }
  return readQuery();
}

export function rememberLeadId(slug: string, leadId: string): void {
  if (!hasWindow() || !slug || !leadId) {
    return;
  }
  try {
    const raw = window.sessionStorage.getItem(LEAD_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    const map =
      parsed && typeof parsed === "object"
        ? (parsed as Record<string, string>)
        : {};
    map[slug] = leadId;
    window.sessionStorage.setItem(LEAD_KEY, JSON.stringify(map));
  } catch {
    // Checkout still works; the webhook just won't carry a lead id.
  }
}

export function readLeadId(slug: string): string | undefined {
  if (!hasWindow()) {
    return undefined;
  }
  try {
    const raw = window.sessionStorage.getItem(LEAD_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return undefined;
    }
    const value = (parsed as Record<string, unknown>)[slug];
    return typeof value === "string" ? value : undefined;
  } catch {
    return undefined;
  }
}
